// src/config/firestoreRecovery.js
//
// Self-heals a poisoned Firestore IndexedDB cache and gives users a safe,
// non-destructive "reset local data" path.
//
// Why this exists: the app previously used a persistent IndexedDB cache, which
// could become corrupted (firebase@9.23.0 "INTERNAL ASSERTION FAILED" / "Failed
// to obtain primary lease" class). When that happened a batch would vanish from
// the grid and the only recovery users knew was clearing the WHOLE browser cache,
// which wipes cookies for every site and logs them out of all 2FA-protected
// services. The app now uses an in-memory cache (see config/firebase.js) so the
// corruption cannot recur, but this module remains to:
//   1. Clean up any LEGACY poisoned IndexedDB left on disk from the old config.
//   2. Provide a one-click "Reset local data" escape hatch.
//
// Order is LOAD-BEARING: terminate(db) MUST run before clearIndexedDbPersistence(db),
// or the latter rejects with 'failed-precondition'. After terminate() the only legal
// call on the instance is clearIndexedDbPersistence(); we never reuse the instance —
// we reload the page so a fresh instance is built.
//
// SCOPE / 2FA SAFETY: clearIndexedDbPersistence wipes ONLY this Firestore database's
// IndexedDB store. It does NOT touch Firebase Auth's separate 'firebaseLocalStorageDb'
// store, localStorage, or cookies — so the user stays logged in here and on every other
// site, unlike the browser-wide cache clear they used to do.

import { terminate, clearIndexedDbPersistence } from 'firebase/firestore';

const RECOVERY_FLAG = 'firestoreCacheRecoveryAttempted';

// Conservative, message-only signatures of the unrecoverable Firestore IndexedDB
// cache-corruption class (verified against firebase@9.23.0 / @firebase/firestore@3.13.0).
// Each token below is a string the SDK actually throws for an UNRECOVERABLE state.
// Intentionally NOT matched, to avoid false-positive full-page reloads (this matcher is
// wired into a global window error net in index.js, so it sees errors from every library):
//   - generic FirestoreError codes 'unavailable'/'internal' — also fire on network blips;
//   - 'IndexedDB transaction … failed' (IndexedDbTransactionError) — transient, code 'unavailable';
//   - the bare phrase 'Unexpected state' — redundant (always prefixed by INTERNAL ASSERTION FAILED).
const CORRUPTION_MESSAGE_RE = /INTERNAL ASSERTION FAILED|primary lease|refusing to open IndexedDB|client has already been terminated/i;

/**
 * @param {unknown} err
 * @returns {boolean} true if this looks like Firestore IndexedDB cache corruption.
 */
export function isFirestoreCorruptionError(err) {
  if (!err) return false;
  const message =
    (err && err.message) ||
    (err && err.reason && err.reason.message) ||
    (typeof err === 'string' ? err : '') ||
    '';
  return CORRUPTION_MESSAGE_RE.test(String(message));
}

function recoveryAlreadyAttempted() {
  try {
    return sessionStorage.getItem(RECOVERY_FLAG) === 'true';
  } catch (_) {
    // sessionStorage can throw in private mode / when storage is disabled.
    return false;
  }
}

function markRecoveryAttempted() {
  try {
    sessionStorage.setItem(RECOVERY_FLAG, 'true');
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Reset the app's local Firestore data and reload with a clean slate.
 *
 * Auto-triggered recovery (force !== true) runs at most ONCE per browser session,
 * guarded by a sessionStorage flag set before the reload — so a persistently failing
 * state can never cause an infinite reload loop. The manual "Reset local data" button
 * passes { force: true } to bypass that guard for a deliberate user action.
 *
 * The IndexedDB clear is best-effort: if it fails (e.g. another tab still holds the
 * legacy DB open), we still reload, because the app uses an in-memory cache and a
 * fresh instance always starts clean.
 *
 * @param {import('firebase/firestore').Firestore} db
 * @param {{ force?: boolean }} [opts]
 * @returns {Promise<boolean>} true if a reload was triggered.
 */
export async function resetFirestoreCache(db, opts = {}) {
  const force = opts.force === true;

  if (!force) {
    if (recoveryAlreadyAttempted()) {
      console.warn('[firestoreRecovery] Recovery already attempted this session; skipping to avoid a reload loop.');
      return false;
    }
    if (!markRecoveryAttempted()) {
      console.warn('[firestoreRecovery] Cannot persist loop-guard flag; aborting auto-recovery to avoid a reload loop.');
      return false;
    }
  } else {
    markRecoveryAttempted();
  }

  // 1) Terminate first so clearIndexedDbPersistence is allowed to run.
  try {
    await terminate(db);
  } catch (err) {
    console.error('[firestoreRecovery] terminate() failed:', err && err.code, err && err.message);
  }

  // 2) Best-effort wipe of any (legacy) Firestore IndexedDB for this project.
  try {
    await clearIndexedDbPersistence(db);
    console.info('[firestoreRecovery] Firestore IndexedDB cleared.');
  } catch (err) {
    // Most likely 'failed-precondition' if another tab still holds the DB open.
    // Harmless with the in-memory cache — we reload anyway and start clean.
    console.error('[firestoreRecovery] clearIndexedDbPersistence() failed (continuing to reload):', err && err.code, err && err.message);
  }

  // 3) Reload to build a fresh, clean Firestore instance.
  console.info('[firestoreRecovery] Reloading to reinitialize Firestore.');
  window.location.reload();
  return true;
}
